import { useContext, useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation, Trans } from "react-i18next";

import { FontSizes, MediaQueries, Spacing } from "@paclife/plds.tokens";
import { Icon } from "@paclife/plds.icon";
import Email from "assets/svg/email.svg";
import { PRIMARY_CEMAIL_TYPE, OTHER_CEMAIL_TYPE } from "constants/index";

import { AppContext } from "context/AppContext";
import { useFlags } from "launchdarkly-react-client-sdk";
import CEmail from "components/CEmail/CEmail";
import Phone from "components/Phone/Phone";
import AddCEmail from "components/CEmail/AddCEmail";
import Card from "common/Card/Card";
import Button from "common/Button/Button";
import Typography from "common/Typography/Typography";
import InfoModal from "components/InfoModal/InfoModal";

const CustomGrid = styled.div`
    display: grid;
    @media (min-width: ${MediaQueries.md}) {
        grid-template-columns: repeat(2, minmax(150px, 1fr));
    }
    @media (min-width: ${MediaQueries.lg}) {
        grid-template-columns: repeat(3, minmax(150px, 1fr));
    }
    gap: 24px;
    margin-bottom: ${Spacing[24]};
`;

const Image = styled.img`
    width: 120px;
    display: none;

    @media (min-width: ${MediaQueries.lg}) {
        display: block;
        grid-column: 3 / 4;
        grid-row: 1 / 3;
        justify-self: center;
    }
`;

const shine = keyframes`
    to {
        background-position-x: -200%;
    }
`;

const Loading = styled.div`
    height: 200px;
    background: rgb(238, 238, 238);
    background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
    border-radius: 5px;
    background-size: 200% 100%;
    animation: 1.5s ${shine} linear infinite;
`;

const LearnMore = styled.div`
    display: flex;
    align-items: center;
    @media (min-width: ${MediaQueries.md}) {
        grid-column: 1/3;
    }
`;

const Text = styled(Typography)`
    margin-left: ${Spacing[4]};
    margin-right: ${Spacing[8]};
`;

const TextButton = styled(Button)`
    font-size: ${FontSizes[400]};
`;


const Notifications = () => {
    const { t } = useTranslation("notification");
    const { 
            cEmails, 
            loadingCEmail,
            account: { factors },
            userSession
          } = useContext(AppContext);

    const userRole = userSession.data?.user?.userRole;
    const ssaRoles = ["ssa_consultant", "ssa_casemanager"];
    const isSsa = ssaRoles.includes(userRole);

          
    const defaultPhoneSelect = { value: "none", label: "None" }

    const [modal, setModal] = useState(false);
    const [phoneOptions, setPhoneOptions] = useState([defaultPhoneSelect]);    
    const [selectedPhone] = useState(defaultPhoneSelect);
    const { rsdUpPhonenotifyprefEn } = useFlags();

    
    useEffect(() => {
        if (factors !== undefined) {
            getPhoneNumber(factors)
        }
    }, [factors]);

    const getPhoneNumber = (data) => {
        const numberArr = [];
        let sltdNumber = "";
        if (data.length > 0) {
            data.forEach(({ factorType, profile, notification, status }) => {
                if (factorType === "phone" && (status === "VERIFIED" || status === "ACTIVE")) { 
                    numberArr.push(profile.phoneNumber);
                    if (notification === true) {
                        sltdNumber = profile?.phoneNumber
                    }
                }
            });

            let tmpOptArr = [];
            tmpOptArr.push({ value: "none", label: "None" });
            for (const element of numberArr) {
                if (element !== "" && element.indexOf("+1", 0) > -1) {
                    tmpOptArr.push({ value: element, label: element });
                }
            }
            setPhoneOptions(tmpOptArr);
            setTimeout(tmpOptArr = [], 1000)
            if (sltdNumber !== "") {
                selectedPhone.value = sltdNumber;
                selectedPhone.label = sltdNumber;
            }
        }
    }

    if (loadingCEmail) {
        return (
            <Card title={t("title")}>
                <CustomGrid>
                    <Loading />
                    <Loading />
                    <Image src={Email} alt="notification emails" />
                </CustomGrid>
            </Card>
        )
    }

    const primaryEmail = cEmails.filter(
        (ce) => ce.statusType === PRIMARY_CEMAIL_TYPE
    )[0]?.emailAddr;
    const otherEmails = cEmails
        .filter((ce) => ce.statusType === OTHER_CEMAIL_TYPE)
        .slice(0, 3);
    return (
        <Card title={t("title")}>
            <CustomGrid>
                <LearnMore>
                    <Icon name="info" width={16} />
                    <Text variant="microcopy">{t(`notification:whatisthis`)}</Text>
                    <TextButton
                        variant="text"
                        onClick={() => setModal(true)}
                        data-track="analytics"
                        data-bname="Learn-More-About-Notifications"
                    >
                        {t(`notification:learnmore`)}
                    </TextButton>
                </LearnMore>
                {primaryEmail && (
                    <CEmail
                        type={PRIMARY_CEMAIL_TYPE}
                        email={primaryEmail.toLowerCase()}
                    />
                )}
                {otherEmails.map(({ statusType, emailAddr }) => {
                    const emlAddr = emailAddr.toLowerCase();
                    return <CEmail
                                type={statusType} 
                                key={emlAddr} 
                                email={emlAddr} 
                            />
                })}
               
                {cEmails.length < 4 && <AddCEmail />}

                { rsdUpPhonenotifyprefEn && 
                    <Phone selected={selectedPhone} options={phoneOptions}/>
                }
                <Image src={Email} alt="contact details image" />
            </CustomGrid>
            {modal && (
                <InfoModal
                    title={t("infoModal.title")}
                    subtitle={
                        <Typography
                            variant="body"
                            align="center"
                            display="block"
                        >
                           { isSsa ? <Trans i18nKey="notification:infoModal.contentSsa" />
                                    : <Trans i18nKey="notification:infoModal.content1" />
                           }
                        </Typography>
                    }
                    closeModal={() => setModal(false)}
                />
            )}
        </Card>
    )
}

export default Notifications;
